import type { ViewerShell } from '../../../shell';
import type { Viewport } from '../../../viewport';
import type { Box } from '../api';
import { frameAt } from '../context';

/**
 * Temporary marks of the Asistente tools, drawn dashed on their image on every render: the area a
 * region question is about (while it is asked) and an assisted-measurement suggestion (until it is
 * accepted or discarded). Drawing only — nothing here touches the annotation store.
 */

export interface Mark {
  id: string;
  instanceUid: string;
  frame: number;
  box: Box;
  label: string;
  color: string;
}

export const MARK_COLORS = { region: '#58a6ff', suggestion: '#00e5ff' } as const;

const DASH = [5, 4];
const LINE_WIDTH = 2;
const LABEL_FONT = '13px Arial, Helvetica, sans-serif';
const LABEL_GAP_PX = 4;
const MIN_LABEL_Y = 14;

const marks = new Map<string, Mark>();
const drawnCount = new WeakMap<HTMLElement, number>();
let shellRef: ViewerShell | null = null;

function marksOn(viewport: Viewport): Mark[] {
  const stack = viewport.currentStack;
  const index = viewport.currentIndex();
  if (!stack || index === undefined) return [];
  const at = frameAt(stack.series, index);
  if (!at) return [];
  return [...marks.values()].filter((m) => m.instanceUid === at.instance.sopInstanceUID && m.frame === at.frame);
}

function drawMark(ctx: CanvasRenderingContext2D, element: HTMLElement, mark: Mark, size: { columns: number; rows: number }): void {
  const [x0, y0, x1, y1] = [mark.box[0] * size.columns, mark.box[1] * size.rows, mark.box[2] * size.columns, mark.box[3] * size.rows];
  const points = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map((p) => window.cornerstone.pixelToCanvas(element, p));
  ctx.strokeStyle = mark.color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.setLineDash(DASH);
  ctx.beginPath();
  points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.stroke();
  if (!mark.label) return;
  const top = points.reduce((a, b) => (b.y < a.y || (b.y === a.y && b.x < a.x) ? b : a));
  ctx.setLineDash([]);
  ctx.font = LABEL_FONT;
  ctx.fillStyle = mark.color;
  ctx.shadowColor = '#000';
  ctx.shadowBlur = 2;
  ctx.fillText(mark.label, top.x, Math.max(MIN_LABEL_Y, top.y - LABEL_GAP_PX));
}

function draw(viewport: Viewport): void {
  const enabled = window.cornerstone.getEnabledElement(viewport.element);
  const image = enabled.image;
  if (!image) return;
  const shown = marksOn(viewport);
  drawnCount.set(viewport.element, shown.length);
  if (shown.length === 0) return;
  const ctx = enabled.canvas.getContext('2d');
  if (!ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (const mark of shown) drawMark(ctx, viewport.element, mark, image);
  ctx.restore();
}

function redraw(): void {
  for (const vp of shellRef?.viewports() ?? []) {
    if (vp.currentImage()) window.cornerstone.updateImage(vp.element);
  }
}

export function setMark(mark: Mark): void {
  marks.set(mark.id, mark);
  redraw();
}

export function removeMark(id: string): void {
  if (marks.delete(id)) redraw();
}

export function hasMark(id: string): boolean {
  return marks.has(id);
}

/** Marks drawn on the last render of viewport `cell` (test hook). */
export function marksDrawnOn(cell: number): number {
  const vp = shellRef?.viewports()[cell];
  return vp ? drawnCount.get(vp.element) ?? 0 : 0;
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  shell.on('image-rendered', ({ viewport }) => draw(viewport));
}
